import React, { useState } from 'react';
import {
  Box, Typography, makeStyles, Link,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { ReactComponent as StructureIcon } from '../../../../layout/icons/smc_fill.svg';
import IDOCReviewView from './IDOCReviewView';
import orders05TxtFile from '../../../../../resources/documents/ORDERS05_NewPO_Business_Mapping.txt';

const useStyles = makeStyles(() => ({
  root: {
    padding: '16px 0 16px 12px',
  },
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      fontWeight: 700,
      fontSize: '0.85rem',
      color: '#333',
      padding: '10px 24px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'nowrap',
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#fafafa' },
    '& td': {
      fontSize: '0.85rem',
      padding: '12px 24px',
      borderBottom: '1px solid #f0f0f0',
      color: '#444',
      verticalAlign: 'middle',
    },
  },
  // Orange stepper
  stepperCell: {
    width: '28px',
    padding: '0 !important',
    verticalAlign: 'middle !important',
  },
  stepperInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    minHeight: '50px',
    justifyContent: 'center',
    position: 'relative',
  },
  stepDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#FF6D00',
    flexShrink: 0,
    zIndex: 1,
  },
  stepLine: {
    width: '2px',
    flex: 1,
    backgroundColor: '#FF6D00',
  },
  idocCell: {
    fontWeight: '700 !important',
  },
  downloadLink: {
    color: '#1976d2',
    fontSize: '0.85rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
  },
  dlIcon: { fontSize: '1.3rem', flexShrink: 0, color: '#1976d2' },
  reviewCell: {
    whiteSpace: 'nowrap',
    padding: '12px 24px !important',
  },
  reviewInner: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: '-2px',
  },
  reviewText: {
    fontSize: '0.82rem',
    color: '#555',
    lineHeight: 1.6,
    cursor: 'pointer',
  },
}));

const MESSAGES = [
  {
    timestamp: '16-MAR-2026\n09:08:22',
    docType: 'IDoc',
    idocType: 'ORDERS05',
    fileName: 'ORDERS05_NewPO_Business_Mapping.txt',
    downloadUrl: orders05TxtFile,
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '16-MAR-2026\n09:05:22',
    docType: 'IDoc',
    idocType: 'LOIPRO01',
    fileName: 'LOIPRO01_ProcessOrder_OperationRouting_PO_4500987654.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '14-MAR-2026\n08:15:10',
    docType: 'XML',
    idocType: 'LOIPRO01',
    fileName: 'LOIPRO01_MaterialComponent_List_PO_4500987654.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '13-MAR-2026\n10:02:08',
    docType: 'XML',
    idocType: 'BATMAS03',
    fileName: 'BATMAS03_BatchMaster_CreationRecord_BM23004587.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '13-MAR-2026\n08:27:40',
    docType: 'IDoc',
    idocType: 'BATMAS03',
    fileName: 'BATMAS03_BatchClassification_Attributes_BM23004587.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '12-MAR-2026\n11:09:17',
    docType: 'XML',
    idocType: 'QMEL',
    fileName: 'QMEL_QualityNotification_Header_QN90014532.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
  {
    timestamp: '12-MAR-2026\n09:44:05',
    docType: 'XML',
    idocType: 'QMEL',
    fileName: 'QMEL_DeviationIncident_Details_QN90014532.txt',
    fieldsRequire: 0,
    fieldsReviewed: 90,
  },
];

const MessagesDashboard = () => {
  const classes = useStyles();
  const [showIDOCReview, setShowIDOCReview] = useState(false);

  if (showIDOCReview) {
    return <IDOCReviewView onBack={() => setShowIDOCReview(false)} />;
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.tableContainer} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell className={classes.stepperCell} />
              <TableCell>Timestamp</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>IDoc/XML</TableCell>
              <TableCell>Download Document</TableCell>
              <TableCell>Review Digitalized Document</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MESSAGES.map((msg, idx) => (
              <TableRow key={idx} className={classes.bodyRow}>
                {/* Stepper */}
                <TableCell className={classes.stepperCell}>
                  <Box className={classes.stepperInner}>
                    {idx > 0 && <Box className={classes.stepLine} />}
                    <Box className={classes.stepDot} />
                    {idx < MESSAGES.length - 1 && <Box className={classes.stepLine} />}
                  </Box>
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  {msg.timestamp.split('\n').map((line, i) => (
                    <span key={i} style={{ display: 'block' }}>{line}</span>
                  ))}
                </TableCell>

                {/* Document Type */}
                <TableCell style={{ fontWeight: 500 }}>{msg.docType}</TableCell>

                {/* IDoc/XML */}
                <TableCell className={classes.idocCell}>{msg.idocType}</TableCell>

                {/* Download */}
                <TableCell>
                  <Link className={classes.downloadLink} onClick={() => {
                    if (msg.downloadUrl) {
                      const a = document.createElement('a');
                      a.href = msg.downloadUrl;
                      a.download = msg.fileName || '';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }
                  }}>
                    <GetAppIcon className={classes.dlIcon} />
                    {msg.fileName}
                  </Link>
                </TableCell>

                {/* Review */}
                <TableCell className={classes.reviewCell}>
                  <Box className={classes.reviewInner}>
                    <GetAppIcon className={classes.dlIcon} style={{ cursor: 'pointer' }} onClick={() => setShowIDOCReview(true)} />
                    <Typography className={classes.reviewText} onClick={() => setShowIDOCReview(true)}>
                      Fields that require review - {String(msg.fieldsRequire).padStart(2, '0')}<br />
                      Fields reviewed - {msg.fieldsReviewed}
                    </Typography>
                    <StructureIcon style={{ cursor: 'pointer', flexShrink: 0, width: '26px', height: '26px' }} onClick={() => setShowIDOCReview(true)} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default MessagesDashboard;
